import { useCallback, useMemo } from 'react';
import { t } from '@ping/helpers';
import { Popup } from '@ping/uikit';
import { getExchangeType, isExchangeTypeEnabled } from '@ping/utils/checkCompany';
import { Button } from '@ping/uikit';

import style from './style.module.scss';

const CommoditiesIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 2L2 7L12 12L22 7L12 2Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path d='M2 17L12 22L22 17' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M2 12L12 17L22 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const CryptoIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path
      d='M12 6 L16 12 L12 18 L8 12 Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      fill='none'
    />
    <line x1='8' y1='12' x2='16' y2='12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
  </svg>
);

const StockIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M3 3v18h18' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M7 16l4-4 3 3 5-8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const ForexIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='9' cy='12' r='7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <circle cx='15' cy='12' r='7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M9 5v14' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M15 5v14' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

interface ExchangeOption {
  key: string;
  label: string;
  icon: JSX.Element;
  storageValue: string | null;
}

interface NoMarketsAvailableModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NoMarketsAvailableModal = ({ isOpen, onClose }: NoMarketsAvailableModalProps) => {
  const currentType = getExchangeType();
  // All available exchange options
  const allExchangeOptions: ExchangeOption[] = useMemo(() => {
    return [
      {
        key: 'crypto',
        label: t('Crypto Currency'),
        icon: <CryptoIcon />,
        storageValue: null,
      },
      {
        key: 'ping',
        label: t('Digital Commodities'),
        icon: <CommoditiesIcon />,
        storageValue: 'ping',
      },
      {
        key: 'stock',
        label: t('Digital Stocks'),
        icon: <StockIcon />,
        storageValue: 'stock',
      },
      {
        key: 'forex',
        label: t('Digital Forex'),
        icon: <ForexIcon />,
        storageValue: 'forex',
      },
    ];
  }, []);

  const handleSelectionChange = useCallback(
    (key: string) => {
      if (!isExchangeTypeEnabled(key)) return;

      const selectedOption = allExchangeOptions.find(opt => opt.key === key);
      if (!selectedOption) return;

      if (selectedOption.storageValue === null) {
        localStorage.removeItem('company');
      } else {
        localStorage.setItem('company', selectedOption.storageValue);
      }

      // Dispatch custom event for same-window listeners
      window.dispatchEvent(new CustomEvent('exchangeTypeChanged'));

      // Close modal and reload
      onClose();
      window.location.href = '/';
    },
    [allExchangeOptions, onClose]
  );

  const currentOption = allExchangeOptions.find(opt => opt.key === currentType) || allExchangeOptions[0];

  return (
    <Popup
      isOpen={isOpen}
      title={t('Markets Coming Soon')}
      onClose={onClose}
      inlineSize='wide'
      className={style['no-markets-modal']}
    >
      <div className={style['modal-content']}>
        <div className={style['message']}>
          <p className={style['message-text']}>
            {t('New markets will be available soon for')} <strong>{currentOption.label}</strong>
          </p>
          <p className={style['message-subtext']}>
            {t('In the meantime, please try switching to a different market type:')}
          </p>
        </div>

        <div className={style['exchange-options']}>
          {allExchangeOptions
            .filter(option => isExchangeTypeEnabled(option.key))
            .map(option => (
              <Button
                key={option.key}
                type={option.key === currentType ? 'primary' : 'secondary'}
                design='general'
                size='large'
                className={style['exchange-option-button']}
                onClick={() => handleSelectionChange(option.key)}
                disabled={option.key === currentType}
              >
                <div className={style['button-content']}>
                  <div className={style['button-icon']}>{option.icon}</div>
                  <span className={style['button-label']}>{option.label}</span>
                </div>
              </Button>
            ))}
        </div>
      </div>
    </Popup>
  );
};
